import {
  resolveSceneSections,
  sectionBackgroundId,
  type Asset,
  type Emotion,
  type Novel,
  type Scene,
  type SceneSection,
  type SectionFrameCue
} from "@lorevok/domain";
import { loadNovel, type NovelListSource } from "../apiClient.js";

/** Cover image shown whenever a novel has no ready cover asset of its own. */
export const fallbackCoverUrl = "/assets/placeholders/cover.webp";

/**
 * User-facing label for a list's data provenance, shared by every page that
 * shows a full "Source:" line. Only the fallback wording is page-specific
 * (the catalog falls back to the demo novel, my-novels to an empty list).
 * The player's compact source chip intentionally uses shorter words.
 */
export function sourceLabel(
  source: NovelListSource,
  loading: boolean,
  fallbackLabel: string
): string {
  if (loading) return "loading…";
  if (source === "api") return "local API";
  if (source === "cache") return "cached copy (server unreachable)";
  return fallbackLabel;
}

export function assetUrl(novel: Novel, assetId: string): string {
  return novel.assets.find((asset) => asset.id === assetId)?.url ?? "";
}

/**
 * The bust sprite URL for a character showing a specific emotion: the matching
 * emotion portrait when one was rendered, otherwise the neutral portrait. Empty
 * string when the character or its asset is missing.
 */
export function characterEmotionSpriteUrl(
  novel: Novel,
  characterId: string,
  emotion: Emotion | undefined
): string {
  const char = novel.characters.find((c) => c.id === characterId);
  if (!char) return "";
  const assetId = (emotion && char.emotion_portraits?.[emotion]) || char.portrait_asset_id;
  return assetUrl(novel, assetId);
}

/**
 * The bust sprite URL a character shows at the START of a scene (its entry
 * expression). `resolveSceneEmotionTimeline` handles mid-scene changes.
 */
export function characterSpriteUrl(novel: Novel, scene: Scene, characterId: string): string {
  return characterEmotionSpriteUrl(novel, characterId, scene.character_emotions?.[characterId]);
}

/** Lowercase + collapse whitespace, for locating a cue anchor within the text. */
function normalizeForMatch(text: string): string {
  return text.toLowerCase().replace(/\s+/gu, " ").trim();
}

/**
 * The index of the reading segment whose text contains the start of `anchor`,
 * given the normalized segments joined into `joined` with each segment's start
 * offset in `starts`. Returns null when the anchor can't be located.
 */
function segmentIndexForAnchor(joined: string, starts: number[], anchor: string): number | null {
  const needle = normalizeForMatch(anchor);
  if (!needle) return null;
  let pos = joined.indexOf(needle);
  if (pos < 0 && needle.length > 24) {
    // Tolerate an anchor kept slightly longer than the prose by matching a prefix.
    pos = joined.indexOf(needle.slice(0, 24));
  }
  if (pos < 0) return null;
  let index = 0;
  for (let i = 0; i < starts.length; i += 1) {
    if (starts[i] <= pos) index = i;
    else break;
  }
  return index;
}

/**
 * Resolves, for each present character, the emotion shown at every reading
 * segment of a scene. Starts from the scene's entry expression
 * (`character_emotions`) and applies each `emotion_cue` from the segment
 * containing its `anchor_text` onward (a later cue for the same character wins).
 * A cue whose anchor can't be located is ignored, so a stale anchor degrades to
 * "no change" instead of switching at the wrong beat. Values are `undefined`
 * where the character shows the neutral / base portrait.
 */
export function resolveSceneEmotionTimeline(
  scene: Scene,
  segments: string[]
): Map<string, Array<Emotion | undefined>> {
  const count = Math.max(segments.length, 1);
  const timeline = new Map<string, Array<Emotion | undefined>>();
  for (const charId of scene.characters_present) {
    timeline.set(charId, new Array(count).fill(scene.character_emotions?.[charId]));
  }

  const cues = scene.emotion_cues;
  if (!cues || cues.length === 0 || segments.length === 0) return timeline;

  const normSegments = segments.map(normalizeForMatch);
  const starts: number[] = [];
  let acc = 0;
  for (const seg of normSegments) {
    starts.push(acc);
    acc += seg.length + 1; // +1 for the space that join(" ") inserts below
  }
  const joined = normSegments.join(" ");

  for (const cue of cues) {
    const lane = timeline.get(cue.char_id);
    if (!lane) continue; // not present in the scene — ignore
    const segIndex = segmentIndexForAnchor(joined, starts, cue.anchor_text);
    if (segIndex === null) continue; // anchor not found — keep the entry emotion
    for (let i = segIndex; i < lane.length; i += 1) lane[i] = cue.emotion;
  }
  return timeline;
}

/**
 * Every image URL a scene renders: its background plus one bust sprite per
 * present character. Used both to compose the stage and to preload the images
 * of upcoming scenes so navigation doesn't flash an empty background.
 */
export function sceneImageUrls(novel: Novel, scene: Scene): string[] {
  const urls: string[] = [];
  const background = assetUrl(novel, scene.background_asset_id);
  if (background) urls.push(background);
  for (const id of scene.characters_present) {
    const url = characterSpriteUrl(novel, scene, id);
    if (url) urls.push(url);
  }
  // Also every mid-scene emotion sprite, so a cue swap mid-read never flashes.
  for (const cue of scene.emotion_cues ?? []) {
    const url = characterEmotionSpriteUrl(novel, cue.char_id, cue.emotion);
    if (url) urls.push(url);
  }
  // Section timeline extras: later locations, per-beat expression overrides and
  // full-frame stills.
  const extra = new Set<string>();
  for (const section of scene.sections ?? []) {
    extra.add(assetUrl(novel, sectionBackgroundId(scene, section)));
    for (const [id, emotion] of Object.entries(section.emotions ?? {})) {
      extra.add(characterEmotionSpriteUrl(novel, id, emotion));
    }
    const still = sectionFrameStill(novel, section);
    if (still) extra.add(still);
  }
  for (const url of extra) if (url && !urls.includes(url)) urls.push(url);
  return urls;
}

/**
 * The full-bleed images of a scene — backgrounds of every beat and full-frame
 * stills — as opposed to character busts. Lets callers size each kind apart.
 */
export function sceneFullBleedUrls(novel: Novel, scene: Scene): Set<string> {
  const urls = new Set<string>([assetUrl(novel, scene.background_asset_id)]);
  for (const section of scene.sections ?? []) {
    urls.add(assetUrl(novel, sectionBackgroundId(scene, section)));
    const still = sectionFrameStill(novel, section);
    if (still) urls.add(still);
  }
  urls.delete("");
  return urls;
}

/** The section's frame-replacement cue together with its asset, when it resolves. */
export function sectionFrame(
  novel: Novel,
  section: SceneSection | undefined
): { cue: SectionFrameCue; asset: Asset } | null {
  const cue = section?.cues?.find((c): c is SectionFrameCue => c.kind === "frame");
  if (!cue) return null;
  const asset = novel.assets.find((a) => a.id === cue.asset_id);
  return asset && asset.url ? { cue, asset } : null;
}

/** URL of a section's full-frame still (never a video), or "" when none. */
function sectionFrameStill(novel: Novel, section: SceneSection): string {
  const frame = sectionFrame(novel, section);
  return frame && frame.asset.type !== "video" ? frame.asset.url : "";
}

const frameCountCache = new WeakMap<Scene, { language: string; count: number }>();

/**
 * How many frames (reading sections) a scene has. Cached per scene object —
 * edits replace the object — so a re-render of a big editor page does not
 * re-segment the prose.
 */
export function sceneFrameCount(novel: Novel, scene: Scene): number {
  const cached = frameCountCache.get(scene);
  if (cached && cached.language === novel.language) return cached.count;
  const count = sceneSections(novel, scene).length;
  frameCountCache.set(scene, { language: novel.language, count });
  return count;
}

/**
 * The reading timeline of a scene in the novel's own language — see
 * `resolveSceneSections`. Stored sections when current, otherwise derived
 * from the prose.
 */
export function sceneSections(novel: Novel, scene: Scene): SceneSection[] {
  return resolveSceneSections(scene, novel.characters, novel.language);
}

const PLAYABLE_AUDIO_EXTENSIONS = [".mp3", ".ogg", ".wav", ".m4a", ".aac", ".flac", ".webm"];

/**
 * Returns the URL of a scene's music asset only when it is a ready, actually
 * playable audio file. Placeholder tracks (e.g. the `.txt` stand-in used by the
 * mock pipeline) return null so the player never renders a dead music control.
 */
export function playableMusicUrl(novel: Novel, assetId: string): string | null {
  const asset = novel.assets.find((candidate) => candidate.id === assetId);
  if (!asset || asset.status !== "ready" || !asset.url) {
    return null;
  }
  const url = asset.url.toLowerCase();
  return PLAYABLE_AUDIO_EXTENSIONS.some((ext) => url.endsWith(ext)) ? asset.url : null;
}

export function toDownloadSlug(title: string): string {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "novel";
}

export async function downloadNovelJson(novelId: string): Promise<void> {
  const result = await loadNovel(novelId);
  if (result.source !== "api") {
    throw new Error("Novel could not be fetched from the API.");
  }
  const { novel } = result;
  const slug = toDownloadSlug(novel.title);
  const filename = `lorevok-${slug}-${novelId}.json`;
  const blob = new Blob([JSON.stringify(novel, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
